import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Injector,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { AppComponentBase } from '@shared/common/app-component-base';
import {
  ActivityDaysServiceProxy,
  AllocationStatusFilterType,
  StaffSubordinatesHierarchiesServiceProxy,
} from '@shared/service-proxies/service-proxies';
import { forkJoin, ReplaySubject, Subject } from '@node_modules/rxjs';
import { FormControl } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { takeUntil } from '@node_modules/rxjs/operators';
import { AppSessionService } from '@shared/common/session/app-session.service';
import { LocalStorageService } from '@shared/utils/local-storage.service';

@Component({
  selector: 'app-filters-bar',
  templateUrl: './filters-bar.component.html',
  styleUrls: ['./filters-bar.component.less'],
})
export class FiltersBarComponent extends AppComponentBase implements OnInit {
  @Input() assignedSupervisorForForemanFilter: boolean;
  @Input() showStatusFilter: boolean = true;
  @Input() showAllocationStatusFilter: boolean = true;
  @Input() showFormStatusFilter: boolean = true;
  @Input() showAssetFilter: boolean = true;
  @Input() showControlAccountFilter: boolean = true;
  @Input() showFormTypeFilter: boolean = true;
  @Input() allocationSheetStatuses: any[]
  @Input() formStatuses: any[]
  @Input() source: string

  @Output() onFiltersChange: EventEmitter<any> = new EventEmitter<any>();
  @ViewChild('creatorSelector', { static: true }) creatorSelector: MatSelect;

  protected _onDestroy = new Subject<void>();

  public creatorFormCtrl: FormControl = new FormControl();
  public creatorFilterFormCtrl: FormControl = new FormControl();
  public filteredCreator: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);

  public assetFormCtrl: FormControl = new FormControl();
  public assetFilterFormCtrl: FormControl = new FormControl();
  public filteredAsset: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);

  public controllingFormCtrl: FormControl = new FormControl();
  public controllingFilterFormCtrl: FormControl = new FormControl();
  public filteredControlling: ReplaySubject<any[]> = new ReplaySubject<any[]>(
    1
  );

  public assignedSupervisorFormCtrl: FormControl = new FormControl();
  public assignedSupervisorFilterFormCtrl: FormControl = new FormControl();
  public filteredAssignedSupervisor: ReplaySubject<any[]> = new ReplaySubject<
    any[]
  >(1);

  allocationStatuses;
  creators;
  assets;
  controlling;
  assignedSupervisor;

  statusFilter = 0;
  allocationStatusFilter = null;
  formStatusFilter = null;
  formTypeFilter = null;
  assetIdFilter = null;
  selectedAssetId = null;
  constructor(
    injector: Injector,
    private _activityDaysServiceProxy: ActivityDaysServiceProxy,
    private _staffHierarchiesServicePRoxy: StaffSubordinatesHierarchiesServiceProxy,
    private cdr: ChangeDetectorRef,
    private _sessionService: AppSessionService,
    private _localStorageService: LocalStorageService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    abp.event.on('app.evt.clearAllocationFilter', () => {
      this.clearFilters();
    });
    let keyValueArray = Object.keys(AllocationStatusFilterType).map((k) => ({
      value: k,
      key: AllocationStatusFilterType[k as any],
    }));
    keyValueArray = keyValueArray.slice(0, keyValueArray.length / 2);
    this.allocationStatuses = keyValueArray;

    const editData = forkJoin({
      creators: this._activityDaysServiceProxy.getCreatorNameValues(),
      assets: this._activityDaysServiceProxy.getAssetsForUserTasks(null),
    });

    editData.subscribe((data) => {
      this.filteredCreator.next(data.creators.slice());
      this.filteredAsset.next(data.assets.slice());

      this.creators = data.creators;
      this.assets = data.assets;
    });

    if (this.assignedSupervisorForForemanFilter) {
      this._staffHierarchiesServicePRoxy
        .getMyOwnSubordinatesContactDetails(null)
        .subscribe((data) => {
          this.filteredAssignedSupervisor.next(
            data.subordinatedStaffDetails.slice()
          );
          this.assignedSupervisor = data.subordinatedStaffDetails;
        });
    }
    this.initiateFiltersValueChanges();
  }

  private initiateFiltersValueChanges() {
    this.creatorFilterFormCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterCreator();
      });

    this.assetFilterFormCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterAssets();
      });

    this.controllingFilterFormCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterControlling();
      });

    if (this.assignedSupervisorForForemanFilter) {
      this.assignedSupervisorFilterFormCtrl.valueChanges
        .pipe(takeUntil(this._onDestroy))
        .subscribe(() => {
          this.filterAssignedSupervisor();
        });
    }
  }

  refresh() {
    this.filtersChange();
  }

  async filtersChange(clearFilter = false) {
    const params = {
      status: this.statusFilter || null,
      creator: this.creatorFormCtrl.value || null,
      assets: this.assetFormCtrl.value || null,
      controlling: this.controllingFormCtrl.value || null,
      supervisors: this.assignedSupervisorFormCtrl.value || null,
      allocationStatusFilter: this.allocationStatusFilter >= 0 ? this.allocationStatusFilter : null,
      formStatusFilter: this.formStatusFilter || null,
      formType: this.formTypeFilter
    };

    const filterKey = `${this.source}-filter-${this._sessionService.user.id}`;
    await this._localStorageService.setItemAsync(filterKey, params);
    this.onFiltersChange.emit(params);
  }

  async clearFilters() {
    this.creatorFormCtrl.setValue(null);
    this.assetFormCtrl.setValue(null);
    this.controllingFormCtrl.setValue(null);
    this.statusFilter = null;
    this.formTypeFilter = null;
    this.allocationStatusFilter = null;
    this.formStatusFilter = null;
    this.filtersChange(true);
  }

  setStatusFilter(status) {
    this.statusFilter = status;
    this.filtersChange();
  }

  setAllocationSheetStatus(status) {
    this.allocationStatusFilter = status;
    this.formTypeFilter =  'Allocation';
    this.filtersChange();
  }

  setFormStatus(status) {
    this.formStatusFilter = status;
    this.formTypeFilter =  'Forms';
    this.filtersChange();
  }

  setFormTypeFilter(type) {
    this.formTypeFilter = type;
    this.filtersChange();
  }

  loadControlling(asset, option) {
    if (this.selectedAssetId === asset.assetId) {
      option.deselect();

      this.assetFormCtrl.reset();
      this.assetFilterFormCtrl.setValue(null);

      this.filteredAsset.next(this.assets.slice());
      this.cdr.detectChanges();

      this.refresh();
      this.selectedAssetId = null;
    } else {
      this.selectedAssetId = asset.assetId;

      this.refresh();

      if (!this.assetFormCtrl.value || !this.assetFormCtrl.value.assetId) {
        this.controllingFormCtrl.reset();
      } else {
        this._activityDaysServiceProxy
          .getControllingAccountsForAsset(asset.assetId, null)
          .subscribe((data) => {
            this.controlling = data;
            this.filteredControlling.next(data.slice());
            this.cdr.detectChanges();
          });
      }
    }
  }

  protected filterCreator() {
    if (!this.creators) {
      return;
    }

    let search = this.creatorFilterFormCtrl.value;
    if (!search) {
      this.filteredCreator.next(this.creators.slice());
      return;
    } else {
      search = search.toLowerCase();
    }

    this.filteredCreator.next(
      this.creators?.filter(
        (creator) => creator.firstName.toLowerCase().indexOf(search) > -1
      )
    );
  }

  protected filterAssignedSupervisor() {
    let search = this.assignedSupervisorFilterFormCtrl.value;
    if (!search) {
      this.filteredAssignedSupervisor.next(this.assignedSupervisor.slice());
      return;
    } else {
      search = search.toLowerCase();
    }

    this.filteredAssignedSupervisor.next(
      this.assignedSupervisor?.filter(
        (assigned) =>
          assigned.contactDetails.emailAddress.toLowerCase().indexOf(search) >
          -1
      )
    );
  }

  protected filterAssets() {
    if (!this.assets) {
      return;
    }

    let search = this.assetFilterFormCtrl.value;
    if (!search) {
      this.filteredAsset.next(this.assets.slice());
      return;
    } else {
      search = search.toLowerCase();
    }

    this.filteredAsset.next(
      this.assets?.filter(
        (asset) => asset.assetId.toLowerCase().indexOf(search) > -1
      )
    );
  }

  protected filterControlling() {
    if (!this.controlling) {
      return;
    }

    let search = this.controllingFilterFormCtrl.value;
    if (!search) {
      this.filteredAssignedSupervisor.next(this.controlling.slice());
      return;
    } else {
      search = search.toLowerCase();
    }

    this.filteredAssignedSupervisor.next(
      this.controlling?.filter(
        (control) =>
          control.controllingAccountAccId.toLowerCase().indexOf(search) > -1
      )
    );
  }
}
